import {
  ConditionalJump,
  QuestionnaireGroupResponse,
  SelectionRequest,
} from '@tiendanube/common';
import { FINISH_MARKER } from './constants';

interface Params {
  currentStep: number;
  response: { options: QuestionnaireGroupResponse[] };
  currentGroup: QuestionnaireGroupResponse;
  currentValues: SelectionRequest[];
  allAnswers: Record<string, SelectionRequest[]>;
}

/**
 * Checks if a conditional jump rule matches the previous answers
 */
function checkConditionalJump(
  rule: ConditionalJump,
  allAnswers: Record<string, SelectionRequest[]>,
  response: { options: QuestionnaireGroupResponse[] },
): boolean {
  const { question: questionPrefix, answer: answerCode } = rule.conditional;

  const referencedQuestion = Object.keys(allAnswers).find((q) =>
    q.startsWith(questionPrefix),
  );

  if (!referencedQuestion) return false;

  const referencedAnswers = allAnswers[referencedQuestion];
  return referencedAnswers.some((ans) => {
    const originalQuestion = response.options.find(
      (opt) => opt.group === referencedQuestion,
    );
    if (!originalQuestion) return false;

    const code = originalQuestion.types.find(
      (t) => t.id === ans.domain_mapping_id,
    )?.code;
    return code?.startsWith(answerCode);
  });
}

/**
 * Finds the first matching conditional jump from the jumpToOn rules
 */
function findConditionalJump(
  jumpToOn: Record<string, string | ConditionalJump> | undefined,
  allAnswers: Record<string, SelectionRequest[]>,
  response: { options: QuestionnaireGroupResponse[] },
): string | undefined {
  if (!jumpToOn) return undefined;

  for (const rule of Object.values(jumpToOn)) {
    if (typeof rule === 'object' && 'conditional' in rule) {
      if (checkConditionalJump(rule, allAnswers, response)) {
        return rule.target;
      }
    }
  }

  return undefined;
}

/**
 * Gets the jump target from string-based rules (empty, skip, or code-based)
 */
function getStringJumpTarget(
  jumpToOn: Record<string, string | ConditionalJump> | undefined,
  selectedId: number | undefined,
  selectedCode: string | undefined,
  selectionCount: number,
): string | undefined {
  if (!jumpToOn) return undefined;

  if (!selectedId && jumpToOn.empty) {
    const emptyRule = jumpToOn.empty;
    return typeof emptyRule === 'string' ? emptyRule : undefined;
  }

  if (selectedCode?.startsWith('skip') && jumpToOn.skip) {
    const skipRule = jumpToOn.skip;
    return typeof skipRule === 'string' ? skipRule : undefined;
  }

  // Only apply code-based jumps when the option is selected alone
  if (selectedCode && selectionCount === 1 && jumpToOn[selectedCode]) {
    const codeRule = jumpToOn[selectedCode];
    return typeof codeRule === 'string' ? codeRule : undefined;
  }

  return undefined;
}

export function getNextStepIndex({
  currentStep,
  response,
  currentGroup,
  currentValues,
  allAnswers,
}: Params): number {
  const selectedId = currentValues[0]?.domain_mapping_id;
  const selectedCode = currentGroup.types.find(
    (t) => t.id === selectedId,
  )?.code;
  const jumpToOn = currentGroup.jumpToOn;

  // Check for conditional jumps first
  const jumpTarget =
    findConditionalJump(jumpToOn, allAnswers, response) ||
    getStringJumpTarget(
      jumpToOn,
      selectedId,
      selectedCode,
      currentValues.length,
    );

  // Check for FINISH marker
  if (jumpTarget === FINISH_MARKER || jumpToOn?.default === FINISH_MARKER) {
    return response.options.length;
  }

  // Find and return target index
  if (jumpTarget) {
    const targetIndex = response.options.findIndex(
      (opt) => opt.group === jumpTarget,
    );
    return targetIndex >= 0 ? targetIndex : currentStep + 1;
  }

  return currentStep + 1;
}
